import { MultiCartDataItems, SelectedCustomerData } from "../utils/models/cart";
import { getCalculatedPrice } from "./business-central";

export const ZAKEKE_BASE = '/apps/errea-b2b';
export interface ZakekeToken {
    access_token: string;
}

export const getZakekeToken = (): Promise<ZakekeToken> => {
    return new Promise((resolve, reject) => {
        const requestOptions: RequestInit = {
            method: 'GET',
        };

        console.log('[getZakekeToken] Fetching token...');
        fetch(`${ZAKEKE_BASE}/zakeke/token`, requestOptions)
            .then((response) => {
                console.log('[getZakekeToken] Response status:', response.status);
                return response.json();
            })
            .then((result) => {
                if (!result.error) {
                    console.log('[getZakekeToken] Token received:', result);
                    resolve(result);
                } else {
                    console.error('[getZakekeToken] Error in result:', result.error);
                    reject(result.error ? result.error : 'error');
                }
            })
            .catch((error) => {
                console.error('[getZakekeToken] Fetch failed:', error);
                reject(error);
            });
    });
};
export interface ZakekeOrderPayload {
    orderCode: string;
    orderDate: string;
    sessionID: string;
    total: number;
    details: {
        orderDetailCode: string;
        sku: string;
        designID: string;
        modelUnitPrice: number;
        designUnitPrice: number;
        quantity: number;
        designModificationID: string;
    }[];
    compositionDetails: {
        orderDetailCode: string;
        composition: string;
        unitPrice: number;
        quantity: number;
    }[];
}

export const createZakekeOrder = async (
    confirmedOrderPID: string,
    cartProducts: MultiCartDataItems
): Promise<ZakekeOrderPayload | null> => {
    const sessionID = `zakeke-session-${confirmedOrderPID}`;

    const selectedCustomerRaw = localStorage.getItem('errea_selectedCustomer');
    if (!selectedCustomerRaw) {
        throw new Error('No selected customer found');
    }
    const selectedCustomer = JSON.parse(selectedCustomerRaw) as SelectedCustomerData;

    const customProducts = cartProducts.items.flatMap((item) => item).filter(variant => !!variant.zakekeDesignId);
    if (!customProducts.length) {
        return null;
    }

    const variantPrices = await Promise.all(
        customProducts.map(async (variant) => {
            const [itemNo, variantCode] = variant.sku.includes('-')
                ? [variant.sku.split('-')[0], variant.sku.split('-').slice(1).join('-')]
                : [variant.sku, ''];

            const pricesResponse = await getCalculatedPrice(
                selectedCustomer.customerNo,
                itemNo,
                variantCode,
            );

            return {
                sku: variant.sku,
                designId: variant.zakekeDesignId ?? '',
                price: pricesResponse?.value?.price ?? 0,
            };
        }),
    );

    const checkoutDataRaw = localStorage.getItem('errea_brochureCheckout');
    const checkoutData = checkoutDataRaw ? JSON.parse(checkoutDataRaw) : null;

    const itemQuantities: Record<string, number> = {};
    if (checkoutData?.items) {
        checkoutData.items.forEach((item: { sku: string; quantity: number }) => {
            itemQuantities[item.sku] = item.quantity;
        });
    }

    let total = 0;
    const details: ZakekeOrderPayload['details'] = [];

    variantPrices.forEach((variantPrice, index) => {
        const quantity = itemQuantities[variantPrice.sku] ?? 0;
        if (quantity === 0) return;

        // TODO: designUnitPrice — set to 0 for now; update when customization surcharge is available
        const designUnitPrice = 0;
        const lineTotal = variantPrice.price * quantity;
        total += lineTotal;

        details.push({
            orderDetailCode: `${confirmedOrderPID}-${index + 1}`,
            sku: variantPrice.sku,
            designID: variantPrice.designId,
            modelUnitPrice: variantPrice.price,
            designUnitPrice,
            quantity,
            designModificationID: '',
        });
    });

    const payload: ZakekeOrderPayload = {
        orderCode: confirmedOrderPID,
        orderDate: new Date().toISOString(),
        sessionID,
        total,
        // NOTE: details is used only for customization generated by the 2D Visual Customizer. Source: Zakeke Support
        details,
        // NOTE: compositionDetails is used only for customization generated by the 3D Product Configurator. Source: Zakeke Support
        compositionDetails: [],
    };

    return new Promise((resolve, reject) => {
        const headers = new Headers();
        headers.append('Content-Type', 'application/json');

        const requestOptions = {
            method: 'POST',
            headers: headers,
            body: JSON.stringify(payload),
            redirect: 'follow' as RequestRedirect,
        };

        fetch(`${ZAKEKE_BASE}/zakeke/orders`, requestOptions)
            .then((response) => response.json())
            .then((result) => {
                if (!result.error) {
                    resolve(result);
                } else {
                    reject(result.error ? result.error : 'error');
                }
            })
            .catch((error) => {
                reject(error);
            });
    });
};
